import { ChevronDown, LoaderCircle, LockKeyhole, LockKeyholeOpen, StickyNote } from "lucide-react";
import { useRef, useState } from "react";
import { RichTextEditor } from "./RichTextEditor";
import { formatRelative } from "../lib/format";
import { noteDraftChanged, startNoteDraft, ticketNoteHtml, type AgentNote, type NoteDraft } from "../lib/ticketNote";

export function TicketAgentNote({ note, canEdit, onSave }: {
  note?: AgentNote;
  canEdit: boolean;
  onSave: (input: { body: string; html: string; expectedRevision: number }) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  // null is locked. An edit session captures its own baseline and is never
  // replaced by realtime updates to note props.
  const [draft, setDraft] = useState<NoteDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const editing = canEdit && draft !== null;
  const dirty = draft !== null && noteDraftChanged(draft);
  const tooLong = !!draft && (draft.body.length > 20000 || draft.html.length > 100000);

  function discard(): boolean {
    if (saving || (dirty && !window.confirm("Discard unsaved changes to this ticket note?"))) return false;
    setDraft(null);
    setError("");
    return true;
  }
  function toggle() {
    if (open) { if (discard()) setOpen(false); }
    else {
      setOpen(true);
      setError("");
      setDraft(canEdit && !note?.body.trim() && !note?.bodyHtml ? startNoteDraft(note) : null);
    }
  }
  async function saveAndLock() {
    if (!editing || !draft || inFlight.current || tooLong) return;
    if (!dirty) { setDraft(null); return; }
    inFlight.current = true;
    setSaving(true);
    setError("");
    try {
      await onSave({ body: draft.body, html: draft.html, expectedRevision: draft.expectedRevision });
      setDraft(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not save note. Your draft has been kept.");
    } finally { inFlight.current = false; setSaving(false); }
  }

  return <section className={`ticket-agent-note${open ? " is-open" : ""}`}>
    <button className="ticket-agent-note__toggle" type="button" disabled={saving} onClick={toggle} aria-expanded={open}>
      <span><StickyNote aria-hidden="true" size={16} /><strong>Ticket note</strong>{!open && note?.body && <em>{note.body}</em>}</span>
      <ChevronDown aria-hidden="true" size={17} />
    </button>
    {open && <div className="ticket-agent-note__editor">
      <div className="ticket-agent-note__controls">
        <span>{editing ? <LockKeyholeOpen aria-hidden="true" size={15} /> : <LockKeyhole aria-hidden="true" size={15} />}{editing ? "Editing · visible to staff only" : "Locked · select text, copy, or open links"}</span>
        {canEdit && <div>
          {editing ? <>
            <button className="button button--secondary button--small" type="button" disabled={saving} onClick={() => discard()}>Cancel</button>
            <button className="button button--secondary button--small" type="button" disabled={saving || tooLong} onClick={() => void saveAndLock()}>{saving ? <LoaderCircle className="spin" aria-hidden="true" size={14} /> : <LockKeyhole aria-hidden="true" size={14} />}{dirty ? "Save & lock" : "Lock"}</button>
          </> : <button className="button button--secondary button--small" type="button" onClick={() => { setDraft(startNoteDraft(note)); setError(""); }}><LockKeyholeOpen aria-hidden="true" size={14} /> Unlock to edit</button>}
        </div>}
      </div>
      {editing ? <RichTextEditor html={draft.html} disabled={saving} ariaLabel="Ticket note" placeholder="Add context, next steps, or anything another agent should know…" onChange={(html, body) => setDraft((current) => current ? { ...current, html, body } : current)} />
        : <div className="ticket-agent-note__read rich-text-content" role="region" aria-label="Ticket note content" dangerouslySetInnerHTML={{ __html: ticketNoteHtml(note) || "<p>No ticket note yet.</p>" }} />}
      <footer><span>{note?.updatedAt ? `Updated ${formatRelative(note.updatedAt)}${note.updatedBy?.name ? ` by ${note.updatedBy.name}` : ""}` : "Visible to staff only"}</span>{editing && <span>{draft.body.length.toLocaleString()}/20,000 characters</span>}</footer>
      {tooLong && <p role="alert">Note is too long. Maximum 20,000 text characters and 100,000 HTML characters.</p>}
      {error && <p role="alert">{error}</p>}
    </div>}
  </section>;
}
